import React, { useState, useEffect, useRef } from 'react';
import Tooltip from '@mui/material/Tooltip';
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  FileDownload as ExportIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import TableToolbar from '../components/TableToolbar';

const ClientVisits = () => {
  const { enqueueSnackbar } = useSnackbar();
  // Total number of columns in the table (S.No, Client Name, Visit Date, etc. - count all TableCell in TableHead)
  const TABLE_COLUMNS = 22;
  const [visits, setVisits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    visit_address: '',
    employee_id: '',
    visit_date: '',
    departure_time: '',
    status: 'scheduled',
    notes: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, searchTerm]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/v1/visits', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm
        }
      });

      console.log('API Response:', response.data);
      console.log('Filtered Data:', response.data.data);
      console.log('Total Items:', response.data.pagination?.total);
      
      // Set the filtered data from API response
      setVisits(response.data.data || []);
      setTotalItems(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Error fetching data', { variant: 'error' });
      setVisits([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (visit = null) => {
    if (visit) {
      setSelectedVisit(visit);
      setFormData({
        client_name: visit.client_name,
        client_email: visit.client_email,
        client_phone: visit.client_phone,
        visit_address: visit.visit_address,
        employee_id: visit.employee_id,
        visit_date: visit.visit_date,
        departure_time: visit.departure_time,
        status: visit.status || 'scheduled',
        notes: visit.notes,
      });
    } else {
      setSelectedVisit(null);
      setFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        visit_address: '',
        employee_id: '',
        visit_date: '',
        departure_time: '',
        status: 'scheduled',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVisit(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedVisit) {
        await api.put(`/v1/visits/${selectedVisit.id}`, formData);
        enqueueSnackbar('Visit updated successfully', { variant: 'success' });
      } else {
        await api.post('/v1/visits', formData);
        enqueueSnackbar('Visit created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving visit:', error);
      enqueueSnackbar(error.response?.data?.message || 'Error saving visit', { variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this visit?')) {
      try {
        await api.delete(`/v1/visits/${id}`);
        enqueueSnackbar('Visit deleted successfully', { variant: 'success' });
        fetchData();
      } catch (error) {
        console.error('Error deleting visit:', error);
        enqueueSnackbar('Error deleting visit', { variant: 'error' });
      }
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/v1/visits/export', {
        params: {
          search: searchTerm,
        },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'client_visits.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('Export successful', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting data:', error);
      enqueueSnackbar('Error exporting data', { variant: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'primary';
      case 'cancelled':
        return 'error';
      case 'in_progress':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleInvoicePaidStatusChange = async (id, newStatus) => {
    try {
      await api.put(`/v1/visits/${id}/invoice-paid-status`, { status: newStatus });
      enqueueSnackbar('Invoice paid status updated successfully', { variant: 'success' });
      fetchData();
    } catch (error) {
      console.error('Error updating invoice paid status:', error);
      enqueueSnackbar('Error updating invoice paid status', { variant: 'error' });
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    
    // Update input value immediately for smooth typing experience
    setSearchInputValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout to update searchTerm after debounce delay
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0); // Reset to first page on search
      setSearchTerm(value); // Update searchTerm, which will trigger useEffect to call fetchData
    }, 500);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <Box 
      sx={{ 
        padding: 0,
        width: '100%',
        backgroundColor: '#f5f5f5',
        margin: 0,
      }}
    >
      <TableToolbar title="Client Visits" />
      
    
      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 1.5, sm: 3 },
          borderRadius: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          backgroundColor: '#fff',
          mb: { xs: 2, sm: 2 }
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={12} md={4} lg={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by client name, company name, or address..."
              value={searchInputValue}
              onChange={handleSearchChange}
              sx={{
                width: '100%',
                '& .MuiInputBase-input::placeholder': {
                  opacity: 1,
                  color: 'rgba(0, 0, 0, 0.6)'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1a237e' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid 
            item 
            xs={12} 
            sm={12} 
            md={8} 
            lg={8} 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'flex-start', sm: 'flex-end' }, 
              gap: { xs: 1, sm: 1 },
              mt: { xs: 0, sm: 0 }
            }}
          >
            <Button
              variant="contained"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              sx={{
                backgroundColor: '#1a237e',
                '&:hover': {
                  backgroundColor: '#0d47a1'
                },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ 
        width: '100%', 
        mx: { xs: 0, sm: 2 },
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflowX: { xs: 'auto', sm: 'hidden' }
      }}>
        <TableContainer>
          <Table>
            <TableHead
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: '#4e0159',
                  color: '#ffffff'
                }
              }}
            >
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 0,
                  paddingRight: 1
                }}>S.No</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Client Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Visit Date</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Visit Address</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Arrival Time</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Departure Time</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Invoice</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Created At</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Worker's Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Client Company Name</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Company Address</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Company Email</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Client Work Rate</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Duration</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Client Mileage Rate</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Mileage</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Invoice Due Amount</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Client Due Date</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Invoice PDF</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: '#1976d2', 
                  color: 'white',
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Attachment</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: '#1976d2', 
                  color: 'white',
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 1
                }}>Invoice Paid Status</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: '#1976d2', 
                  color: 'white',
                  borderBottom: 'none',
                  paddingLeft: 1,
                  paddingRight: 2
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && visits.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMNS} align="center" sx={{ py: 8 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary">
                        Loading visits...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {visits.map((visit, index) => (
                    <TableRow 
                      key={visit.visit_id || visit.id || visit.client_visits_id || `visit-${index}`}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{visit.client_name}</TableCell>
                  <TableCell>{formatDate(visit.visit_date)}</TableCell>
                  <TableCell>
                    <Box sx={{ 
                      maxWidth: 200, 
                      maxHeight: 65, 
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '4px',
                      },
                    }}>
                      {visit.visit_address}
                    </Box>
                  </TableCell>
                  <TableCell>{visit.arrival_time || '00:00:00'}</TableCell>
                  <TableCell>{visit.departure_time || '00:00:00'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={visit.pdf_file ? 'Invoice Sent' : 'Invoice Not Sent'} 
                      color={visit.pdf_file ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(visit.created_at)}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{`${visit.emp_first_name} ${visit.emp_last_name}`}</TableCell>
                  <TableCell>{visit.client_company_name}</TableCell>
                  <TableCell>
                    <Box sx={{ 
                      maxWidth: 200, 
                      maxHeight: 65, 
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '4px',
                      },
                    }}>
                      {visit.company_address}
                    </Box>
                  </TableCell>
                  <TableCell>{visit.company_email}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>${visit.client_work_rate}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{parseFloat(visit.duration || 0).toFixed(2)}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>${visit.client_mileage_rate}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{visit.mileage_status === 1 ? parseFloat(visit.distance || 0).toFixed(2) : ''}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>${parseFloat(visit.client_due_bal || 0).toFixed(2)}</TableCell>
                  <TableCell>{formatDate(visit.due_date_calc)}</TableCell>
                  <TableCell>  
                    {visit.pdf_file ? (
                      <a 
                        href={`${process.env.REACT_APP_GOOGLE_CLOUD_BUCKET_PDF}/${visit.pdf_file}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        <PdfIcon sx={{ 
                          fontSize: 28, 
                          color: '#d32f2f',
                          '&:hover': {
                            color: '#b71c1c'
                          }
                        }} />
                      </a>
                    ) : <Tooltip title="No PDF available">
    <span>
      <PdfIcon sx={{ 
        fontSize: 28,
        color: '#d32f2f',
        opacity: 0.3,
        cursor: 'not-allowed'
      }} />
    </span>
  </Tooltip>}
                  </TableCell>
                  <TableCell>
                    {visit.image ? (
                      <Button
                        variant="outlined"
                        size="small"
                        href={`${process.env.REACT_APP_GOOGLE_CLOUD_BUCKET}/${visit.image}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        sx={{
                          borderColor: '#1a237e',
                          color: '#1a237e',
                          '&:hover': {
                            borderColor: '#0d47a1',
                            backgroundColor: 'rgba(26, 35, 126, 0.04)'
                          }
                        }}
                      >
                        Download
                      </Button>
                    ) : <Tooltip title="No attachment">
  <span>
    <Button
      variant="outlined"
      size="small"
      disabled
      sx={{
        borderColor: '#1a237e',
        color: '#1a237e',
        opacity: 0.5,
        '&:hover': {
          borderColor: '#1a237e',
          backgroundColor: 'transparent'
        }
      }}
    >
      Download
    </Button>
  </span>
</Tooltip>

}
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={visit.invoice_paid_status === 1}
                      onChange={() => handleInvoicePaidStatusChange(visit.visit_id, visit.invoice_paid_status === 1 ? 0 : 1)}
                      sx={{
                        color: '#4e0159',
                        '&.Mui-checked': {
                          color: '#4e0159',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleDelete(visit.visit_id)}
                      sx={{
                        color: '#d32f2f',
                        '&:hover': {
                          backgroundColor: 'rgba(211, 47, 47, 0.04)'
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                  ))}
                  {loading && visits.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={TABLE_COLUMNS} align="center" sx={{ py: 4, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                        <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" color="text.secondary">
                            Updating...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid #e0e0e0'
          }}
        />
      </Paper>
    </Box>
  );
};

export default ClientVisits; 